import { useQuery, useMutation, UseQueryResult, UseMutationResult, useQueryClient } from 'react-query';
import { useApiClient } from '../hooks/useApiClient';

export type AdminUserSummary = {
  id: string;
  email: string;
  name: string;
  department?: string | null;
  role: 'employee' | 'manager' | 'finance' | 'admin' | 'super_admin' | string;
  roles?: string[]; // multi-role support
  is_active: boolean;
  is_approver: boolean;
  approval_scope: 'none' | 'team' | 'all' | string;
  approval_limit?: string | number | null; // server may return decimal as string
};

export type AdminUserListResponse = {
  items: AdminUserSummary[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type ListUsersParams = {
  page?: number;
  size?: number;
  search?: string;
  role?: string;
  department?: string;
  approver?: boolean;
};

export type UpdateUserPayload = Partial<{
  role: string;
  department: string | null;
  is_active: boolean;
  is_approver: boolean;
  approval_scope: 'none' | 'team' | 'all';
  approval_limit: number | null;
}>;

export type CreateUserPayload = {
  email: string;
  name: string;
  role?: 'employee' | 'manager' | 'finance' | 'admin' | 'super_admin' | string;
  department?: string | null;
  is_active?: boolean;
  is_approver?: boolean;
  approval_scope?: 'none' | 'team' | 'all';
  approval_limit?: number | null;
  auth_type?: 'db_email_otp'; // fixed for this flow
};

export const buildQueryString = (params: Record<string, any>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      q.set(k, String(v));
    }
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

type ApiLike = {
  get: <T = any>(url: string, config?: any) => Promise<T>;
  put: <T = any>(url: string, data?: any, config?: any) => Promise<T>;
  post: <T = any>(url: string, data?: any, config?: any) => Promise<T>;
};

export const listAdminUsers = async (api: ApiLike, params: ListUsersParams = {}): Promise<AdminUserListResponse> => {
  const query = buildQueryString({
    page: params.page ?? 1,
    size: params.size ?? 20,
    search: params.search,
    role: params.role,
    department: params.department,
    approver: params.approver,
  });
  const data = await api.get<AdminUserListResponse>(`/admin/users/${query}`);
  return data;
};

export const updateAdminUser = async (
  api: ApiLike,
  userId: string,
  payload: UpdateUserPayload
): Promise<AdminUserSummary> => {
  const data = await api.put<AdminUserSummary>(`/admin/users/${userId}`, payload);
  return data;
};

export const createAdminUser = async (
  api: ApiLike,
  payload: CreateUserPayload
): Promise<AdminUserSummary> => {
  const body: CreateUserPayload = {
    auth_type: 'db_email_otp',
    role: 'employee',
    is_active: true,
    is_approver: false,
    approval_scope: 'none',
    ...payload,
  };
  const data = await api.post<AdminUserSummary>(`/admin/users/`, body);
  return data;
};

// React Query hooks
export const useAdminUsers = (params: ListUsersParams = {}): UseQueryResult<AdminUserListResponse> => {
  const api = useApiClient();
  return useQuery(['admin-users', params], () => listAdminUsers(api, params), { keepPreviousData: true, staleTime: 30_000 });
};

export const useUpdateAdminUser = (): UseMutationResult<AdminUserSummary, unknown, { userId: string; payload: UpdateUserPayload }> => {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation(({ userId, payload }) => updateAdminUser(api, userId, payload), {
    onSuccess: () => {
      qc.invalidateQueries('admin-users');
    },
  });
};

export const useCreateAdminUser = (): UseMutationResult<AdminUserSummary, unknown, CreateUserPayload> => {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation((payload) => createAdminUser(api, payload), {
    onSuccess: () => {
      qc.invalidateQueries('admin-users');
    },
  });
};

// --- Roles API ---
export const getUserRoles = async (api: ApiLike, userId: string): Promise<string[]> => {
  return api.get<string[]>(`/admin/users/${userId}/roles`);
};

export const setUserRoles = async (api: ApiLike, userId: string, roles: string[]): Promise<string[]> => {
  return api.put<string[]>(`/admin/users/${userId}/roles`, { roles });
};

export const useUserRoles = (userId?: string) => {
  const api = useApiClient();
  return useQuery(['admin-user-roles', userId], () => getUserRoles(api, userId as string), {
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
};

export const useSetUserRoles = () => {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation(({ userId, roles }: { userId: string; roles: string[] }) => setUserRoles(api, userId, roles), {
    onSuccess: (_data, vars) => {
      qc.invalidateQueries(['admin-user-roles', vars.userId]);
      qc.invalidateQueries('admin-users');
    },
  });
};
